import { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Alert,
} from 'reactstrap';
import { isApiError } from '../../api/errors';
import { OneShotSecretReveal } from '../../components/OneShotSecretReveal';
import { CopyChip } from '../../components/CopyChip';
import { useCreateUser } from './userMutations';

/**
 * Drei-Stufen-Flow:
 *   1. Form (Email, Name, Role, AccountType, optional Password, B2B-Block)
 *   2. Success-Bestätigung mit User-ID + Kundennummer (Copy-Chips)
 *   3. OneShotSecretReveal mit `generatedPassword` — NUR wenn kein Passwort übergeben
 *
 * Spec: orbit-specs/done/user-crud-admin.md
 */
export function UserCreateModal({ isOpen, onClose }) {
  const [form, setForm] = useState(initial());
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const create = useCreateUser();

  const isBusiness = form.accountType === 'business';

  const set = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const reset = () => {
    setForm(initial());
    setError(null);
    setResult(null);
  };

  const finishAndClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.email.trim()) {
      setError('E-Mail ist erforderlich.');
      return;
    }
    if (isBusiness && !form.companyName.trim()) {
      setError('Firmenname ist bei Business-Konten erforderlich.');
      return;
    }

    const payload = {
      email: form.email.trim(),
      displayName: form.displayName.trim() || undefined,
      role: form.role,
      accountType: form.accountType,
      password: form.password.trim() || undefined,
      ...(isBusiness
        ? {
            companyName: form.companyName.trim(),
            vatId: form.vatId.trim() || undefined,
            billingCountry: form.billingCountry.trim() || undefined,
            billingCity: form.billingCity.trim() || undefined,
            billingZip: form.billingZip.trim() || undefined,
            billingStreet: form.billingStreet.trim() || undefined,
          }
        : {}),
    };

    try {
      const res = await create.mutateAsync(payload);
      setResult(res);
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Anlegen fehlgeschlagen.');
    }
  };

  // Stufe 1+2 in einer Modal, Stufe 3 (OneShot) als eigene Modal danach
  if (result?.generatedPassword) {
    return (
      <>
        <SuccessModal
          isOpen
          user={result.user}
          onContinue={() => {
            // Result behält user, generatedPassword wird im Reveal gehandled
            // Hier nur die Success-Modal schließen → Reveal kommt automatisch
          }}
        />
        <OneShotSecretReveal
          isOpen
          label="Initial-Passwort"
          secret={result.generatedPassword}
          confirmPhrase="Ich habe das Passwort sicher an den Mandanten übermittelt"
          onAcknowledged={finishAndClose}
        />
      </>
    );
  }

  if (result) {
    // Kein generiertes Passwort — Operator hat eigenes übergeben, nur Success zeigen
    return <SuccessModal isOpen user={result.user} onContinue={finishAndClose} />;
  }

  return (
    <Modal isOpen={isOpen} toggle={create.isPending ? undefined : finishAndClose} size="lg" backdrop="static">
      <ModalHeader toggle={create.isPending ? undefined : finishAndClose}>
        Neuer Benutzer
      </ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="newEmail">E-Mail *</Label>
                <Input id="newEmail" type="email" value={form.email} onChange={set('email')} required />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="newName">Anzeigename</Label>
                <Input id="newName" value={form.displayName} onChange={set('displayName')} />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="newRole">Rolle</Label>
                <Input id="newRole" type="select" value={form.role} onChange={set('role')}>
                  <option value="customer">customer</option>
                  <option value="staff">staff</option>
                  <option value="admin">admin</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="newType">Konto-Typ</Label>
                <Input id="newType" type="select" value={form.accountType} onChange={set('accountType')}>
                  <option value="private">private</option>
                  <option value="business">business</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="newPw">Passwort</Label>
                <Input
                  id="newPw"
                  type="password"
                  value={form.password}
                  onChange={set('password')}
                  placeholder="leer = generiert"
                  autoComplete="new-password"
                />
                <small className="text-muted">
                  {form.password
                    ? 'Eigenes Passwort — wird nicht angezeigt.'
                    : 'Wird vom Server generiert und nach Anlage einmalig sichtbar.'}
                </small>
              </FormGroup>
            </Col>
          </Row>

          {isBusiness && (
            <>
              <hr />
              <h6 className="text-muted small text-uppercase">B2B-Block</h6>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="newCompany">Firma *</Label>
                    <Input id="newCompany" value={form.companyName} onChange={set('companyName')} required />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="newVat">USt-IdNr</Label>
                    <Input id="newVat" value={form.vatId} onChange={set('vatId')} placeholder="DE123456789" />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="3">
                  <FormGroup>
                    <Label for="newCountry">Land</Label>
                    <Input id="newCountry" value={form.billingCountry} onChange={set('billingCountry')} placeholder="DE" maxLength={2} />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label for="newZip">PLZ</Label>
                    <Input id="newZip" value={form.billingZip} onChange={set('billingZip')} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="newCity">Stadt</Label>
                    <Input id="newCity" value={form.billingCity} onChange={set('billingCity')} />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label for="newStreet">Straße</Label>
                <Input id="newStreet" value={form.billingStreet} onChange={set('billingStreet')} />
              </FormGroup>
            </>
          )}

          {error && <Alert color="danger">{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={finishAndClose} disabled={create.isPending}>
            Abbrechen
          </Button>
          <Button color="primary" type="submit" disabled={create.isPending}>
            {create.isPending ? 'Anlegen…' : 'Anlegen'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

function SuccessModal({ isOpen, user, onContinue }) {
  return (
    <Modal isOpen={isOpen} backdrop="static" keyboard={false}>
      <ModalHeader>Benutzer angelegt</ModalHeader>
      <ModalBody>
        <p className="text-success mb-3">
          ✓ User wurde erfolgreich erstellt. Kopiere ID und Kundennummer für die
          Verknüpfung im Mandanten-System (z. B. valiKey):
        </p>
        <div className="mb-2">
          <small className="text-muted text-uppercase">User-ID</small>
          <div>
            <CopyChip value={String(user.id)} label="User-ID" />
          </div>
        </div>
        <div className="mb-2">
          <small className="text-muted text-uppercase">Kundennummer</small>
          <div>
            <CopyChip value={user.customerNumber} label="Kundennummer" />
          </div>
        </div>
        <div className="mb-2">
          <small className="text-muted text-uppercase">E-Mail</small>
          <div>{user.email}</div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={onContinue}>
          Weiter
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function initial() {
  return {
    email: '',
    displayName: '',
    role: 'customer',
    accountType: 'private',
    password: '',
    companyName: '',
    vatId: '',
    billingCountry: '',
    billingCity: '',
    billingZip: '',
    billingStreet: '',
  };
}
